import type { Plan, SyncOperation, SyncStatus } from '@/domain/types';
import { createId, nowIso } from '@/domain/helpers';
import { getDb } from '@/storage/db';

const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 2_000;
const MAX_BACKOFF_MS = 5 * 60_000;

export function resolveConflictLww<T extends { updatedAt: string; clientUpdatedAt?: string }>(
  local: T,
  remote: T,
): T {
  const localTs = local.clientUpdatedAt ?? local.updatedAt;
  const remoteTs = remote.clientUpdatedAt ?? remote.updatedAt;
  return remoteTs >= localTs ? remote : local;
}

export type SyncErrorKind = 'transient' | 'permanent';

export function classifySyncError(message: string): SyncErrorKind {
  const lower = message.toLowerCase();
  if (
    lower.includes('failed to fetch') ||
    lower.includes('network') ||
    lower.includes('timeout') ||
    lower.includes('timed out') ||
    lower.includes('offline') ||
    lower.includes('econn') ||
    lower.includes('fetch failed') ||
    lower.includes('503') ||
    lower.includes('502') ||
    lower.includes('504') ||
    lower.includes('429')
  ) {
    return 'transient';
  }
  return 'permanent';
}

export function userFacingSyncError(kind: SyncErrorKind): string {
  return kind === 'transient'
    ? 'Temporary connection problem'
    : 'Could not sync some changes';
}

export function computeNextRetryAt(attempts: number, now = Date.now()): string {
  const exp = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.max(0, attempts - 1));
  const jitter = Math.floor(Math.random() * 250);
  return new Date(now + exp + jitter).toISOString();
}

export function isOpDue(op: SyncOperation, now = Date.now(), force = false): boolean {
  // Force always retries — including permanent exhausted ops that otherwise
  // stick the UI on sync_error while newer successful plan writes already landed.
  if (force) return true;
  if (op.errorKind === 'permanent' && op.attempts >= MAX_ATTEMPTS) return false;
  if (!op.nextRetryAt) return true;
  return new Date(op.nextRetryAt).getTime() <= now;
}

/** Compare client/server timestamps regardless of space vs `T` ISO formatting. */
export function compareClientTimestamps(a: string, b: string): number {
  const ta = parseFlexibleTimestamp(a);
  const tb = parseFlexibleTimestamp(b);
  if (ta !== null && tb !== null) return ta - tb;
  return a < b ? -1 : a > b ? 1 : 0;
}

function parseFlexibleTimestamp(raw: string): number | null {
  const normalized = raw
    .trim()
    .replace(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)/, '$1T$2')
    .replace(/([+-]\d{2})$/, '$1:00')
    .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const t = Date.parse(normalized);
  return Number.isNaN(t) ? null : t;
}

async function findQueuedForEntity(
  entityType: SyncOperation['entityType'],
  entityId: string,
): Promise<SyncOperation[]> {
  // Full table scan — do not rely on Dexie entityId index alone. A missing /
  // unrebuilt index makes where('entityId') throw or miss rows; after a
  // successful server write that left the op in place and markSyncError fired.
  const all = await getDb().syncQueue.toArray();
  return all.filter((row) => row.entityType === entityType && row.entityId === entityId);
}

/** Coalesce: one queue row per entity; refresh payload; clear retry gate on new local edit. */
export async function enqueueSync(
  op: Omit<SyncOperation, 'id' | 'createdAt' | 'attempts' | 'lastError' | 'lastErrorDetail' | 'errorKind' | 'nextRetryAt'>,
): Promise<void> {
  const db = getDb();
  const existing = (await findQueuedForEntity(op.entityType, op.entityId))[0];

  if (existing) {
    await db.syncQueue.put({
      ...existing,
      action: op.action,
      payload: op.payload,
      createdAt: nowIso(),
      attempts: 0,
      lastError: null,
      lastErrorDetail: null,
      errorKind: null,
      nextRetryAt: null,
    });
    return;
  }

  const item: SyncOperation = {
    id: createId(),
    createdAt: nowIso(),
    attempts: 0,
    lastError: null,
    lastErrorDetail: null,
    errorKind: null,
    nextRetryAt: null,
    ...op,
  };
  await db.syncQueue.put(item);
}

export async function listSyncQueue(): Promise<SyncOperation[]> {
  return getDb().syncQueue.orderBy('createdAt').toArray();
}

export async function removeSyncOp(id: string): Promise<void> {
  await getDb().syncQueue.delete(id);
}

export async function removeSyncOpsForEntity(
  entityType: SyncOperation['entityType'],
  entityId: string,
): Promise<number> {
  const rows = await findQueuedForEntity(entityType, entityId);
  await Promise.all(rows.map((row) => getDb().syncQueue.delete(row.id)));
  return rows.length;
}

/**
 * After a confirmed server write (or LWW skip), always drop this op and any
 * sibling rows for the same entity — never leave lastError stuck after success.
 */
export async function settleSyncedOp(op: SyncOperation): Promise<void> {
  try {
    await removeSyncOpsForEntity(op.entityType, op.entityId);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[twyme sync] removeSyncOpsForEntity failed', err);
    }
  }
  await removeSyncOp(op.id);
}

/** On manual/force retry: lift backoff and reset exhausted permanent ops so a
 *  server-side fix (e.g. migration) can clear sync_error on the next flush. */
export async function clearRetryGates(): Promise<void> {
  const queue = await listSyncQueue();
  const db = getDb();
  for (const op of queue) {
    const exhausted = op.errorKind === 'permanent' && op.attempts >= MAX_ATTEMPTS;
    if (op.nextRetryAt || exhausted) {
      await db.syncQueue.put({
        ...op,
        attempts: exhausted ? 0 : op.attempts,
        lastError: exhausted ? null : op.lastError,
        lastErrorDetail: exhausted ? null : op.lastErrorDetail,
        errorKind: exhausted ? null : op.errorKind,
        nextRetryAt: null,
      });
    }
  }
}

export async function markSyncError(id: string, detail: string): Promise<void> {
  const op = await getDb().syncQueue.get(id);
  if (!op) return;
  const kind = classifySyncError(detail);
  const attempts = op.attempts + 1;
  if (import.meta.env.DEV) {
    console.warn('[twyme sync]', op.entityType, op.entityId, kind, detail);
  }
  await getDb().syncQueue.put({
    ...op,
    attempts,
    lastError: userFacingSyncError(kind),
    lastErrorDetail: detail.slice(0, 500),
    errorKind: kind,
    nextRetryAt: kind === 'permanent' && attempts >= MAX_ATTEMPTS ? null : computeNextRetryAt(attempts),
  });
}

export async function mergeRemotePlans(local: Plan[], remote: Plan[]): Promise<Plan[]> {
  const map = new Map<string, Plan>();
  for (const p of local) map.set(p.id, p);
  for (const r of remote) {
    const existing = map.get(r.id);
    if (!existing) {
      map.set(r.id, r);
    } else {
      map.set(r.id, resolveConflictLww(existing, r));
    }
  }
  return Array.from(map.values());
}

export type SyncState = SyncStatus;

export function deriveSyncState(opts: {
  online: boolean;
  syncing: boolean;
  pendingCount: number;
  hasPermanentError: boolean;
  hasRetryableError: boolean;
}): SyncState {
  if (!opts.online) return 'offline';
  if (opts.syncing) return 'syncing';
  if (opts.hasPermanentError) return 'sync_error';
  if (opts.hasRetryableError) return 'retrying';
  if (opts.pendingCount > 0) return 'changes_pending';
  return 'synced';
}

export function summarizeQueue(queue: SyncOperation[]): {
  pendingCount: number;
  hasPermanentError: boolean;
  hasRetryableError: boolean;
} {
  const pendingCount = queue.length;
  const hasPermanentError = queue.some(
    (q) => q.errorKind === 'permanent' && q.attempts >= MAX_ATTEMPTS,
  );
  const hasRetryableError = queue.some(
    (q) =>
      Boolean(q.lastError) &&
      !(q.errorKind === 'permanent' && q.attempts >= MAX_ATTEMPTS),
  );
  return { pendingCount, hasPermanentError, hasRetryableError };
}

export const SYNC_MAX_ATTEMPTS = MAX_ATTEMPTS;
